import ko from "knockout";
import _ from "lodash";
import Promise from "bluebird";
import moment from "moment";
import qualityManagementService from "services/qualityManagementService";
import notificationService from "services/notificationService";
import userService from "services/userService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import loggingService from "services/loggingService";
import toastService from "services/toastService";
import qualityAuthorization from "services/qualityAuth";
import navHelper from "services/navHelper";
import ViewModel from "viewModels/ViewModel";
import errorCallbackFactory from "util/errorCallbackFactory";
import EvaluationStatus from "enums/composer/EvaluationStatus";
import EvaluationSubmittedEventType from "enums/EvaluationSubmittedEventType";
import toasts from "services/toastService";
import {
    setQueryParams,
    getDecodedQueryParamsFromUri,
    getDecodedQueryParamsFromWindow,
} from "util/uriUtil";
import { FULL_DATE_SHORT_TIME_OPTIONS } from "util/DateTimeHelper";

const oneMinuteMs = 60 * 1000;
const oneHourMs = 60 * oneMinuteMs;
const oneDayMs = 24 * oneHourMs;
const maxCharCount = 60;
const totalScoreField = "totalScore";
const totalCriticalScoreField = "totalCriticalScore";

const EvaluationSummaryView = ViewModel.extend(function (
    conversationId,
    hasParticipantToEvaluate,
    deleteEvaluationCallback
) {
    const self = this;
    let evaluationStatus;
    self.hasParticipantToEvaluate = hasParticipantToEvaluate;
    self.evaluationSummaryGrid = ko.observableArray();
    self.newEvaluationHref = navHelper.buildInteractionHash(
        "admin",
        conversationId(),
        "qualitySummary/evaluation/new"
    );
    self.newCalibrationHref = navHelper.buildInteractionHash(
        "admin",
        conversationId(),
        "qualitySummary/calibration/new"
    );
    self.canAddEvaluation = ko.observable(
        qualityAuthorization.canAddEvaluation()
    );
    self.canAddCalibration = ko.observable(
        qualityAuthorization.canAddCalibration()
    );
    self.canDeleteEvaluation = ko.observable(
        qualityAuthorization.canDeleteEvaluation()
    );
    self.isDeleteEvaluationInProgress = ko.observable(false);
    let EVALUATION_SUMMARY_COLUMNS;

    const isAssigneeNameToggleEnabled = featureToggleService.isEnabled(
        FeatureToggle.EVALUATION_ASSIGNEE
    );
    const isDisputeToggleEnabled = featureToggleService.isEnabled(
        FeatureToggle.EVALUATION_DISPUTES
    );

    const isAddScoreToQualitySummaryToggleEnabled =
        featureToggleService.isEnabled(
            FeatureToggle.WEM_1378_ADD_SCORE_TO_QUALITY_SUMMARY
        );

    EVALUATION_SUMMARY_COLUMNS = [
        {
            label: "evaluationSummary.formNameTitle",
            id: "formName",
        },
    ];

    if (isAddScoreToQualitySummaryToggleEnabled) {
        EVALUATION_SUMMARY_COLUMNS.push({
            label: "evaluationSummary.evaluationFormTypeTitle",
            id: "evaluationFormType",
        });
    }

    EVALUATION_SUMMARY_COLUMNS.push({
        label: "evaluationSummary.dateTitle",
        id: "assignedDate",
    });

    if (isAddScoreToQualitySummaryToggleEnabled) {
        EVALUATION_SUMMARY_COLUMNS.push({
            label: "evaluationSummary.agentTitle",
            id: "agent",
        });
    }

    EVALUATION_SUMMARY_COLUMNS.push({
        label: "evaluationSummary.evaluatorTitle",
        id: "evaluator",
    });

    if (isAssigneeNameToggleEnabled) {
        EVALUATION_SUMMARY_COLUMNS.push({
            label: "evaluationSummary.assigneeTitle",
            id: "assignee",
        });
    }

    EVALUATION_SUMMARY_COLUMNS.push({
        label: "evaluationSummary.statusTitle",
        id: "status",
    });

    if (isAddScoreToQualitySummaryToggleEnabled) {
        EVALUATION_SUMMARY_COLUMNS.push(
            {
                label: "evaluationSummary.scoreTitle",
                id: "score",
            },
            {
                label: "evaluationSummary.criticalScoreTitle",
                id: "criticalScore",
            }
        );
    }

    EVALUATION_SUMMARY_COLUMNS.push({
        label: "evaluationSummary.agentHasReadTitle",
        id: "agentHasRead",
    });

    if (isDisputeToggleEnabled) {
        EVALUATION_SUMMARY_COLUMNS.push({
            label: "evaluationSummary.disputeCount",
            id: "disputeCount",
        });
    }

    EVALUATION_SUMMARY_COLUMNS.push({
        label: "evaluationSummary.agentComment",
        id: "agentComment",
    });

    EVALUATION_SUMMARY_COLUMNS.push({
        label: "evaluationSummary.calibration",
        id: "calibration",
    });

    const notificationTopic = [];
    const topic = `v2.conversations.${conversationId()}.evaluations`;

    const getAssigneeNameFromEvaluation = (evaluation, assigneeApplicable) => {
        const unknownUser = ko.i18n.renderString(
            "evaluationSummary.evaluator.unknown"
        );
        if (assigneeApplicable === false) {
            return ko.i18n.renderString("evaluationSummary.assignee.na");
        }
        if (evaluation?.assignee) {
            return evaluation.assignee.name ?? unknownUser;
        }
        return ko.i18n.renderString("evaluationSummary.assignee.none");
    };

    const isSparkV4Enabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1128_IDUI_SPARK_V4
    );

    const formatEvaluationScore = function (answers, scoreField, status) {
        const isCriticalScore = scoreField === totalCriticalScoreField;
        const maxCriticalScore = answers?.maxTotalRawCriticalScore;

        if (isCriticalScore && !maxCriticalScore) {
            return ko.i18n.renderString("evaluationSummary.score.none");
        }

        return answers &&
            answers[scoreField] != null &&
            (status.toUpperCase() === EvaluationStatus.FINISHED ||
                status.toUpperCase() === EvaluationStatus.INREVIEW)
            ? ko.i18n.formatNumber(answers[scoreField] / 100, {
                  style: "percent",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              })
            : ko.i18n.renderString("evaluationSummary.score.none");
    };

    /**
     *  If the EventType of the evaluation is Deleted the we will remove just that evaluation from the list
     *  If the EventType is Submitted, just update the existing rows information
     *  If none of the conditions above are met, then the evaluation being passed to the function should be a newly
     *      assigned evaluation. In this case we will reload the grid to get the new entry.
     */
    self.handleEvaluationNotification = function (evaluationNotification) {
        /**
         * This function is triggered for every update related to evaluations in a conversation
         * Each notification holds detail of the affected evaluation along with its EventType
         * Each notification doesn't hold usual params like "pageNumber", "pageCount", "pageSize", "total" which is required for Quality Summary Tab
         */
        let updatedRows = [];
        loggingService.info(
            "Received evaulationNotification:",
            evaluationNotification.id,
            " : ",
            evaluationNotification.eventType
        ); //TODO: Cleanup after REC-6799 Capture data from received notification to debug for automation failure.
        if (
            evaluationNotification.eventType ===
            EvaluationSubmittedEventType.DELETED
        ) {
            /**
             * Deleted selected rows and update collection
             * If sparkV4 FT is enabled, subtract by one from total available evaluations in gridOptions
             * This is done to update total count of evaluations which is displayed in pagination.
             */
            updatedRows = _.reject(self.evaluationSummaryGrid().rows(), {
                evaluationId: evaluationNotification.id,
            });
            if (isSparkV4Enabled) {
                const newTotal = Math.max(0, self.gridOptions().total - 1);
                self.gridOptions({
                    ...self.gridOptions(),
                    total: newTotal,
                });
            }
            self.evaluationSummaryGrid().rows(updatedRows);
        } else {
            if (
                isSparkV4Enabled &&
                evaluationNotification.eventType ===
                    EvaluationSubmittedEventType.ASSIGNED
            ) {
                /**
                 * If sparkV4 FT is enabled and new evaluation created, add by one from total available evaluations in gridOptions
                 * This is done to update total count of evaluations which is displayed in pagination.
                 */
                self.gridOptions({
                    ...self.gridOptions(),
                    total: self.gridOptions().total + 1,
                });
            }
            const isEvaluatorChangeToggleEnabled =
                featureToggleService.isEnabled(
                    FeatureToggle.EVALUATION_EVALUATOR_CHANGE_FEATURE_TOGGLE_NAME
                );
            const expandParams = ["evaluator"];
            if (isAssigneeNameToggleEnabled) {
                expandParams.push("assignee");
            }
            if (isAddScoreToQualitySummaryToggleEnabled) {
                expandParams.push("agent");
                expandParams.push("evaluationForm");
            }
            qualityManagementService
                .getAssignedEvaluation(
                    conversationId(),
                    evaluationNotification.id,
                    { expand: expandParams, ...self.gridOptions() }
                )
                .then(function (response) {
                    if (response) {
                        const updatedRowIndex = _.findIndex(
                            self.evaluationSummaryGrid().rows(),
                            { evaluationId: evaluationNotification.id }
                        );

                        let evaluatorName;
                        if (response?.systemSubmitted === true) {
                            evaluatorName = ko.i18n.renderString(
                                "evaluationSummary.evaluator.virtual.supervisor"
                            );
                        } else {
                            evaluatorName =
                                response.evaluator?.name ||
                                ko.i18n.renderString(
                                    "evaluationSummary.evaluator.unknown"
                                );
                        }

                        const assigneeName = getAssigneeNameFromEvaluation(
                            response,
                            evaluationNotification.assigneeApplicable
                        );
                        if (updatedRowIndex !== -1) {
                            /**
                             * Update the row based on submitted evaluations
                             */
                            const rowToUpdate = self
                                .evaluationSummaryGrid()
                                .rows()[updatedRowIndex];
                            rowToUpdate.status = getStatusString(
                                evaluationNotification.status
                            );
                            if (isAddScoreToQualitySummaryToggleEnabled) {
                                rowToUpdate.evaluationFormType =
                                    getEvaluationFormType(response);
                            }
                            rowToUpdate.agentHasRead =
                                evaluationNotification.agentHasRead
                                    ? "evaluationSummary.agentHasRead.yes"
                                    : "evaluationSummary.agentHasRead.no";
                            rowToUpdate.disputeCount =
                                evaluationNotification.calibration
                                    ? ko.i18n.renderString(
                                          "evaluationSummary.disputeCount.na"
                                      )
                                    : evaluationNotification.disputeCount ||
                                      ko.i18n.renderString(
                                          "evaluationSummary.disputeCount.none"
                                      );
                            rowToUpdate.assignee = assigneeName;

                            // Update score and criticalScore
                            if (isAddScoreToQualitySummaryToggleEnabled) {
                                rowToUpdate.score = formatEvaluationScore(
                                    response.answers,
                                    totalScoreField,
                                    evaluationNotification.status
                                );
                                rowToUpdate.criticalScore =
                                    formatEvaluationScore(
                                        response.answers,
                                        totalCriticalScoreField,
                                        evaluationNotification.status
                                    );
                                rowToUpdate.agent =
                                    response.agent?.name ??
                                    ko.i18n.renderString(
                                        "evaluationSummary.agent.unknown"
                                    );
                            }

                            if (isEvaluatorChangeToggleEnabled) {
                                rowToUpdate.evaluator = evaluatorName;
                            }

                            updatedRows = _.pull(
                                self.evaluationSummaryGrid().rows(),
                                self.evaluationSummaryGrid().rows()[
                                    updatedRowIndex
                                ]
                            );
                            updatedRows.splice(updatedRowIndex, 0, rowToUpdate);
                            self.evaluationSummaryGrid().loading(true);
                            // Update the grid with the new rows
                            self.evaluationSummaryGrid().rows(updatedRows);
                            self.evaluationSummaryGrid().loading(false);
                        } else {
                            // Evaluation not on current page
                            const currentPageSize =
                                self.gridOptions().pageSize || 25; // Default page size is 25
                            const total =
                                self.gridOptions().total != null
                                    ? self.gridOptions().total
                                    : currentPageSize; // Fallback so we reload when total is missing (err on side of refresh)

                            if (total >= currentPageSize) {
                                // At or above full page: refresh to avoid duplication and keep in sync
                                self.evaluationSummaryGrid().loading(true);
                                self.refreshEvaluationSummaryGrid(
                                    self.gridOptions()
                                );
                            } else {
                                // Page is not nearing full: add row without refresh
                                const conversationId = response.conversation.id;
                                const evaluationStatus = getStatusString(
                                    response.status
                                );
                                const evaluationAssignedDate = moment(
                                    response.assignedDate
                                ).toDate();
                                const createdDate = response.createdDate
                                    ? moment(response.createdDate)
                                    : null;
                                const formName = response.evaluationForm.name;
                                const evaluationHref = navHelper.asHostUrl(
                                    navHelper.buildInteractionHash(
                                        "admin",
                                        conversationId,
                                        `qualitySummary/?evaluationId=${response.id}`
                                    )
                                );
                                const newRow = buildRow(
                                    response,
                                    conversationId,
                                    formName,
                                    evaluationStatus,
                                    evaluationAssignedDate,
                                    createdDate,
                                    evaluatorName,
                                    assigneeName,
                                    evaluationHref
                                );
                                if (newRow) {
                                    const currentRows = self
                                        .evaluationSummaryGrid()
                                        .rows();
                                    currentRows.unshift(newRow);
                                    self.evaluationSummaryGrid().loading(true);
                                    // Update the grid with the new rows
                                    self.evaluationSummaryGrid().rows(
                                        currentRows
                                    );
                                    self.evaluationSummaryGrid().loading(false);
                                }
                            }
                        }
                    }
                });
        }
    };

    const getEvaluationFormType = function (evaluation) {
        return evaluation?.evaluationForm?.evaluationFormType === "Automated"
            ? "evaluationSummary.evaluationFormType.automated"
            : "evaluationSummary.evaluationFormType.manual";
    };

    const getStatusString = function (status) {
        let evaluationStatus;
        // We have to call toUpperCase because status may be from a
        // query or notification, and they use different casing
        switch (status.toUpperCase()) {
            case EvaluationStatus.PENDING:
                evaluationStatus = "pending";
                break;
            case EvaluationStatus.INPROGRESS:
                evaluationStatus = "inProgress";
                break;
            case EvaluationStatus.FINISHED:
                evaluationStatus = isDisputeToggleEnabled
                    ? "completed"
                    : "finished";
                break;
            case EvaluationStatus.INREVIEW:
                evaluationStatus = "inReview";
                break;
            case EvaluationStatus.RETRACTED:
                evaluationStatus = "retracted";
                break;
            default:
                evaluationStatus = "finished";
        }
        return `evaluationSummary.${evaluationStatus}`;
    };

    const buildRow = function (
        evaluation,
        conversationId,
        formName,
        evaluationStatus,
        evaluationAssignedDate,
        evaluationCreatedDate,
        displayName,
        assigneeName,
        evaluationHref,
        isFormDeleted
    ) {
        let handleRowClick;
        if (isFormDeleted) {
            handleRowClick = function () {
                toasts.notifyMajorAlert(
                    ko.i18n.renderString(
                        "evaluationSummary.formNotFound.cannotExpand"
                    )
                );
            };
        } else {
            handleRowClick = function (bindingContext, event) {
                event.preventDefault();
                const currentParams = getDecodedQueryParamsFromWindow();
                const newQueryParams = {
                    ...getDecodedQueryParamsFromUri(evaluationHref),
                    ["assignmentPanel"]: false,
                };
                if (currentParams?.qualityFormVersion) {
                    newQueryParams["qualityFormVersion"] =
                        currentParams.qualityFormVersion;
                }
                const path = setQueryParams(evaluationHref, newQueryParams);
                navHelper.navigateTo(
                    isAssigneeNameToggleEnabled && isDisputeToggleEnabled
                        ? path
                        : evaluationHref,
                    undefined,
                    false
                );
            };
        }
        const newRow = {
            conversationId: conversationId,
            evaluationId: evaluation.id,
            formName:
                formName ||
                ko.i18n.renderString(
                    "evaluationSummary.formNotFound.formDeleted"
                ),
            assignedDate: evaluationAssignedDate,
            evaluator: displayName,
            status: evaluationStatus,
            createdDate: evaluationCreatedDate,
            disputeCount: evaluation.calibration
                ? ko.i18n.renderString("evaluationSummary.disputeCount.na")
                : evaluation.disputeCount ||
                  ko.i18n.renderString("evaluationSummary.disputeCount.none"),
            agentComment:
                evaluation.answers && evaluation.answers.agentComments
                    ? evaluation.answers.agentComments.length > maxCharCount
                        ? evaluation.answers.agentComments.slice(
                              0,
                              maxCharCount
                          ) + "..."
                        : evaluation.answers.agentComments
                    : "",
            hasCalibrationLabel: evaluation.calibration
                ? "evaluationSummary.isEvaluationPartOfCalibration.yes"
                : "evaluationSummary.isEvaluationPartOfCalibration.no",
            handleRowClick,
            enableDeletedTooltip: isFormDeleted,
        };
        newRow.formattedAssignedDate = ko.i18n.formatDate(
            evaluationAssignedDate,
            null,
            null,
            FULL_DATE_SHORT_TIME_OPTIONS
        );
        if (isAddScoreToQualitySummaryToggleEnabled) {
            newRow.evaluationFormType = getEvaluationFormType(evaluation);
            newRow.score = formatEvaluationScore(
                evaluation.answers,
                totalScoreField,
                evaluation.status
            );
            newRow.criticalScore = formatEvaluationScore(
                evaluation.answers,
                totalCriticalScoreField,
                evaluation.status
            );
            newRow.agent =
                evaluation.agent?.name ??
                ko.i18n.renderString("evaluationSummary.agent.unknown");
        }

        if (isAssigneeNameToggleEnabled) {
            newRow.assignee = assigneeName;
        }
        if (isDisputeToggleEnabled && evaluation.calibration) {
            newRow.agentHasRead = "evaluationSummary.agentHasRead.na";
        } else {
            newRow.agentHasRead = evaluation.agentHasRead
                ? "evaluationSummary.agentHasRead.yes"
                : "evaluationSummary.agentHasRead.no";
        }
        return newRow;
    };

    self.listEntities = function (evaluations) {
        let conversationId,
            evaluationAssignedDate,
            createdDate,
            displayName,
            assigneeName,
            formName,
            evaluationHref,
            isFormDeleted;

        const rows = _.filter(evaluations, function (evaluation) {
            return !evaluation.redacted;
        }).map(function (evaluation) {
            conversationId = evaluation.conversation.id;
            evaluationStatus = getStatusString(evaluation.status);
            evaluationAssignedDate = moment(evaluation.assignedDate).toDate();
            createdDate = evaluation.createdDate
                ? moment(evaluation.createdDate)
                : null;

            if (evaluation?.systemSubmitted === true) {
                displayName = ko.i18n.renderString(
                    "evaluationSummary.evaluator.virtual.supervisor"
                );
            } else {
                displayName =
                    evaluation.evaluator?.name ||
                    ko.i18n.renderString("evaluationSummary.evaluator.unknown");
            }
            assigneeName = getAssigneeNameFromEvaluation(
                evaluation,
                evaluation.assigneeApplicable
            );
            formName = evaluation.evaluationForm.name;
            evaluationHref = navHelper.asHostUrl(
                navHelper.buildInteractionHash(
                    "admin",
                    conversationId,
                    `qualitySummary/?evaluationId=${evaluation.id}`
                )
            );
            isFormDeleted = !evaluation.evaluationForm.name;
            const newRow = buildRow(
                evaluation,
                conversationId,
                formName,
                evaluationStatus,
                evaluationAssignedDate,
                createdDate,
                displayName,
                assigneeName,
                evaluationHref,
                isFormDeleted
            );
            return newRow;
        });
        self.evaluationSummaryGrid().rows(rows);
    };

    self.createNewEvaluation = function () {
        navHelper.navigateTo(self.newEvaluationHref, undefined, false);
    };

    self.createNewCalibration = function () {
        navHelper.navigateTo(self.newCalibrationHref, undefined, false);
    };

    self.showDeleteEvaluationsPrompt = function () {
        const modal = document.getElementById("deleteEvaluationsPrompt");
        if (modal && typeof modal.showModal === "function") {
            modal.showModal();
        }
    };

    self.closeDeleteEvaluationsPrompt = function () {
        const modal = document.getElementById("deleteEvaluationsPrompt");
        if (modal && typeof modal.close === "function") {
            modal.close();
        }
    };

    self.deleteSelectedRows = function () {
        self.isDeleteEvaluationInProgress(true);
        self.closeDeleteEvaluationsPrompt();
        const selectedRows = self.evaluationSummaryGrid().selectedRows();
        const deletedRows = [];
        // selectedRows will return a single object when one row is selected
        // it will return an array when multiple rows are selected.
        _.each(selectedRows, function (row) {
            deletedRows.push(
                deleteEvaluationCallback(row.conversationId, row.evaluationId)
            );
        });

        Promise.allSettled(deletedRows).then(function (promiseResults) {
            let failedResults = 0;
            //Check for a single error on row delete
            _.each(promiseResults, function (deletedRowPromise) {
                if (deletedRowPromise.isRejected()) {
                    failedResults++;
                    loggingService.error("Failed to delete evaluation", {
                        settledValue: deletedRowPromise._settledValue,
                    });
                } else {
                    const deletedEvaluation = deletedRowPromise._settledValue();
                    if (deletedEvaluation && deletedEvaluation.id) {
                        const deletedEvaluationId = deletedEvaluation.id;
                        const remainingSelectedRows = self
                            .evaluationSummaryGrid()
                            .selectedRows()
                            .filter((row) => {
                                return row.evaluationId !== deletedEvaluationId;
                            });

                        self.evaluationSummaryGrid().selectedRows(
                            remainingSelectedRows
                        );
                    }
                }
            });

            if (
                failedResults !== promiseResults.length &&
                failedResults !== 0
            ) {
                toastService.notifyCriticalAlert(
                    "evaluationSummary.error.delete",
                    {
                        bodyMessage: "evaluationSummary.error.delete.body",
                        messageData: failedResults,
                    }
                );
            } else if (failedResults === promiseResults.length) {
                toastService.notifyCriticalAlert(
                    "evaluationSummary.error.delete.all"
                );
            }
            self.isDeleteEvaluationInProgress(false);
        });
    };

    self.refreshEvaluationSummaryGrid = function (incomingOptions) {
        return qualityManagementService
            .getConversationEvaluations(conversationId(), incomingOptions)
            .then(function (response) {
                const params = ["pageNumber", "pageCount"];
                if (isSparkV4Enabled) {
                    params.push("pageSize");
                    params.push("total");
                }
                const newOptions = _.assign(
                    {},
                    _.pick(response, params),
                    incomingOptions
                );
                self.gridOptions(newOptions);
                self.listEntities(response.entities);
                return null;
            })
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "playback.evaluationSummaryRequest",
                })
            )
            .finally(function () {
                self.evaluationSummaryGrid().loading(false);
            });
    };

    self.gridOptions = ko.observable({
        pageNumber: 1,
    });

    // Add the appropriate information to the dataTable
    self.evaluationSummaryGrid({
        columns: EVALUATION_SUMMARY_COLUMNS,
        rows: ko.observableArray([]),
        selectedRows: ko.observableArray([]),
        loading: ko.observable(true),
        onDataOptionsChange: self.refreshEvaluationSummaryGrid,
        dataOptions: self.gridOptions,
    });

    self.handlePageChange = function (_data, event) {
        if (isSparkV4Enabled) {
            self.evaluationSummaryGrid().loading(true);
            self.gridOptions({
                ...self.gridOptions(),
                pageNumber: event.detail.currentPage,
                pageSize: event.detail.itemsPerPage,
            });
            const dataOptions = ko.unwrap(self.gridOptions());
            self.evaluationSummaryGrid().dataOptions(dataOptions);
            delete dataOptions.total;
            self.refreshEvaluationSummaryGrid(dataOptions);
        }
    };

    self.handleRowSelection = function (_data, event) {
        const selectedRows = [];
        if (event.detail.selectedRowIds.length > 0) {
            for (let i = 0; i < event.detail.selectedRowIds.length; i++) {
                const rowId = parseInt(
                    _.replace(event.detail.selectedRowIds[i], "form-row-", "")
                );
                if (self.evaluationSummaryGrid().rows()[rowId]) {
                    selectedRows.push(
                        self.evaluationSummaryGrid().rows()[rowId]
                    );
                }
            }
        }
        self.evaluationSummaryGrid().selectedRows(selectedRows);
    };

    // Look for row selection to handle evaluation deletion
    self.canDeleteRows = self.computed(function () {
        return (
            qualityAuthorization.canDeleteEvaluation() &&
            !_.isEmpty(self.evaluationSummaryGrid().selectedRows())
        );
    });

    notificationService
        .subscribe(topic, self.handleEvaluationNotification)
        .finally(function () {
            self.refreshEvaluationSummaryGrid(self.gridOptions());
        });

    self.onDispose = function () {
        notificationService.batchUnsubscribe(topic);
    };
});

export default EvaluationSummaryView;
